import UnreadIndicator from '@atoms/UnreadIndicator';
import type { DrawerContentComponentProps } from '@react-navigation/drawer';
import { DrawerContentScrollView } from '@react-navigation/drawer';
import { Colors, TextBold, TextReg } from '@sunrise-ui/primitives';
import { router, usePathname, type Href } from 'expo-router';
import { Drawer } from 'expo-router/drawer';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { useGetTotalUnreadCount } from '@queries/conversations';
import { ConnectionType } from '@sunrise-ui/api/messenger';

const CustomDrawerItem = ({
  path,
  label,
  numUnread,
}: {
  path: Href;
  label: string;
  numUnread?: number;
}) => {
  const currentPath = usePathname();
  const isActivePath = currentPath === path;

  return (
    <Pressable
      style={[styles.navItem, isActivePath && styles.activeNavItem]}
      onPress={() => {
        router.navigate(path);
      }}
    >
      <View style={styles.navItemContent}>
        {isActivePath ? (
          <TextBold style={styles.navItemText}>{label}</TextBold>
        ) : (
          <TextReg style={styles.navItemText}>{label}</TextReg>
        )}
        <UnreadIndicator count={numUnread} />
      </View>
    </Pressable>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomDrawerContent = (props: DrawerContentComponentProps) => {
  const { data: connectedUnreadCount } = useGetTotalUnreadCount(
    ConnectionType.All,
  );
  // TODO: Add back in once we have introduced convo data
  // const { data: introducedUnreadCount } = useGetTotalUnreadCount(
  //   ConnectionType.Introduced,
  // );
  const introducedUnreadCount = 0;
  return (
    <DrawerContentScrollView {...props} style={styles.drawerContent}>
      <View style={styles.sectionHeaderContainer}>
        <TextReg style={styles.sectionHeaderText}>Messages</TextReg>
      </View>
      <CustomDrawerItem
        path="/inbox/all"
        label="All"
        numUnread={connectedUnreadCount}
      />
      <CustomDrawerItem
        path="/inbox/connected"
        label="Connected"
        numUnread={connectedUnreadCount}
      />
      <CustomDrawerItem
        path="/inbox/introduced"
        label="Introduced"
        numUnread={introducedUnreadCount}
      />
    </DrawerContentScrollView>
  );
};

export default function Layout() {
  const screenOptions = {
    drawerActiveTintColor: Colors.text,
    drawerType: 'front' as const,
    overlayColor: Colors.blackOpacity05,
    drawerStyle: {
      backgroundColor: Colors.purple0,
      borderRadius: 0,
    },
  };

  return (
    <Drawer
      screenOptions={screenOptions}
      backBehavior="history"
      drawerContent={(props) => <CustomDrawerContent {...props} />}
    >
      <Drawer.Screen name="all" options={{ headerShown: false }} />
      <Drawer.Screen name="connected" options={{ headerShown: false }} />
      <Drawer.Screen name="introduced" options={{ headerShown: false }} />
    </Drawer>
  );
}

const styles = StyleSheet.create({
  drawerContent: {
    flex: 1,
  },
  sectionHeaderContainer: {
    paddingHorizontal: 24,
    paddingBottom: 8,
    marginTop: 16,
  },
  sectionHeaderText: {
    fontSize: 16,
    color: Colors.text,
  },
  navItem: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    marginHorizontal: 12,
    marginVertical: 2,
    borderRadius: 8,
  },
  activeNavItem: {
    backgroundColor: Colors.purple1opacity50,
  },
  navItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  navItemText: {
    fontSize: 14,
    color: Colors.text,
  },
});
